export type SpecialistCategory = 'geral' | 'academico' | 'comercial' | 'juridico';

export interface SpecialistDefinition {
  value: string;
  label: string;
  category: SpecialistCategory;
}

// "Especialista" reaproveita o campo Proposal.type já existente (string livre) como chave.
// Este catálogo é a lista curada exibida na criação/edição de proposta e usada para
// selecionar preset de campos (conceptMapFieldPresets.ts) e config de raciocínio
// (specialistReasoningConfig no backend, espelhado aqui só para exibição).
// Labels alinhados à lista oficial do card #111 — só o texto exibido mudou, `value`
// continua igual para não quebrar dados salvos (Proposal.type).
export const SPECIALIST_CATALOG: SpecialistDefinition[] = [
  { value: 'Editais', label: 'Propostas para Editais', category: 'geral' },
  { value: 'Licitações Públicas', label: 'Propostas para Licitações', category: 'juridico' },
  { value: 'Propostas Comerciais', label: 'Propostas Comerciais', category: 'comercial' },
  { value: 'Planos e Projetos Acadêmicos', label: 'Planos e Projetos Acadêmicos', category: 'academico' },
  { value: 'Planejamento de Ensino', label: 'Planejamento de Ensino', category: 'academico' },
  { value: 'Artigo Científico', label: 'Artigos Científicos e Trabalhos Acadêmicos', category: 'academico' },
  { value: 'Contratos', label: 'Instrumentos Contratuais', category: 'juridico' },
  { value: 'Petições Jurídicas', label: 'Petições e Requerimentos Jurídicos', category: 'juridico' },
  { value: 'Pareceres Técnicos/Jurídicos', label: 'Pareceres Técnicos e Jurídicos', category: 'juridico' },
  { value: 'Planos de Negócios para Investidores', label: 'Planos de Negócios', category: 'comercial' },
  { value: 'Planos Estratégicos e de Ação', label: 'Planos Estratégicos de Ação', category: 'geral' },
  { value: 'Relatórios', label: 'Relatórios Técnicos e Financeiros', category: 'geral' },
];

const LEGACY_SPECIALIST_LABELS: Record<string, string> = {
  // Tipos antigos continuam aceitos no banco, mas não voltam a aparecer com
  // nomenclatura diferente na interface.
  Planos: 'Planos de Negócios',
  'Planos Estratégicos de Ação': 'Planos Estratégicos de Ação',
};

// Espelha LEGACY_SPECIALIST_MAP (core/config.py) — resolve valores legados para a
// especialidade canônica antes de qualquer lookup de blocos/reasoning, para que
// propostas antigas (Proposal.type = "Planos") caiam na config canônica correta.
const LEGACY_SPECIALIST_MAP: Record<string, string> = {
  Planos: 'Planos de Negócios para Investidores',
  'Planos Estratégicos de Ação': 'Planos Estratégicos e de Ação',
};

function normalizeSpecialist(value?: string): string | undefined {
  if (!value) return value;
  return LEGACY_SPECIALIST_MAP[value] ?? value;
}

export function getSpecialistLabel(value?: string): string {
  return SPECIALIST_CATALOG.find(s => s.value === value)?.label
    || LEGACY_SPECIALIST_LABELS[value || '']
    || value
    || 'Propostas para Editais';
}

// Nível de raciocínio é sempre escolha do usuário — nenhum Especialista trava o seletor
// (espelha resolve_effective_reasoning_level em core/config.py, que não força mais nada).
export function specialistRequiresAdvancedReasoning(_proposalType?: string): boolean {
  return false;
}

// Espelha SPECIALIST_BLOCKS_CONFIG (core/config.py) — só para render da UI (quais sub-abas da
// Revisão aparecem). O backend nunca depende deste espelho; cada endpoint de bloco é isolado.
export const DEFAULT_SPECIALIST_BLOCKS = ['enquadramento', 'documental', 'merito'];

// Blocos por especialista alinhados ao Quadro Resumo do Framework de Revisão do
// Documento (seção 3, 12/08/2026). Risco/Exposição só se aplica a Propostas Comerciais
// e Instrumentos Contratuais no doc — removido dos demais que tinham por engano.
// Consistência Argumentativa (Petições/Pareceres) e Originalidade (Artigo Científico)
// não entram aqui ainda por não terem bloco implementado (ver Task 5 do backlog).
const SPECIALIST_BLOCKS_CONFIG: Record<string, string[]> = {
  'Editais': ['enquadramento', 'documental', 'merito', 'consistencia_interna'],
  'Licitações Públicas': ['enquadramento', 'documental', 'merito', 'consistencia_interna'],
  'Propostas Comerciais': ['enquadramento', 'documental', 'merito', 'consistencia_interna', 'risco_exposicao'],
  'Planos de Negócios para Investidores': ['merito', 'consistencia_interna'],
  'Artigo Científico': ['enquadramento', 'documental', 'merito'],
  'Contratos': ['enquadramento', 'risco_exposicao'],
  'Pareceres Técnicos/Jurídicos': ['enquadramento'],
  'Petições Jurídicas': ['enquadramento', 'documental'],
  'Relatórios': ['enquadramento', 'documental', 'consistencia_interna'],

  // Especialidades adicionadas ao catálogo oficial.
  'Planos e Projetos Acadêmicos': ['enquadramento', 'documental', 'consistencia_interna'],
  'Planejamento de Ensino': ['enquadramento', 'documental', 'consistencia_interna'],
  'Planos Estratégicos e de Ação': ['enquadramento', 'consistencia_interna'],
};

export function getSpecialistBlocks(proposalType?: string): string[] {
  if (!proposalType) return DEFAULT_SPECIALIST_BLOCKS;
  const canonical = normalizeSpecialist(proposalType) ?? proposalType;
  return SPECIALIST_BLOCKS_CONFIG[canonical] ?? DEFAULT_SPECIALIST_BLOCKS;
}
